/**
 * Helpers for beehiiv / MailerLite MCP OAuth in a full browser tab.
 * The original Newsfiliate tab stays put and polls (or receives postMessage) for completion.
 */

export const MCP_CONNECT_MESSAGE_SOURCE = 'nf-mcp-connect'
/** Named window so retry reuses the same tab when possible. */
export const POPUP_WINDOW_NAME = 'nfMcpConnect'
export const AUTH_WINDOW_NAME = POPUP_WINDOW_NAME
/** Set on the auth tab before leaving NF so we can recognize it after COOP severs opener. */
export const MCP_AUTH_WINDOW_STORAGE_KEY = 'nf_mcp_auth_window'

const TAB_PROVIDER_TYPES = new Set(['beehiiv', 'mailerlite'])

/**
 * beehiiv / MailerLite open OAuth in a new tab (not same-tab redirect).
 * @param {string} [providerType]
 * @returns {boolean}
 */
export function usesConnectTab(providerType) {
  return TAB_PROVIDER_TYPES.has(String(providerType || '').trim().toLowerCase())
}

/** @deprecated use usesConnectTab */
export function usesConnectPopup(providerType) {
  return usesConnectTab(providerType)
}

/**
 * Mark this browsing context as the MCP auth tab (survives cross-site hops back to NF).
 * @param {Window|null} win
 */
export function markAuthWindow(win) {
  if (!win || win.closed) return
  try {
    win.name = AUTH_WINDOW_NAME
  } catch {
    // ignore
  }
  try {
    win.sessionStorage?.setItem(MCP_AUTH_WINDOW_STORAGE_KEY, '1')
  } catch {
    // about:blank / cross-origin may refuse; window.name is the backup
  }
}

/**
 * @returns {boolean}
 */
export function isMcpAuthWindow() {
  if (typeof window === 'undefined') return false
  if (window.opener && window.opener !== window) return true
  if (window.name === AUTH_WINDOW_NAME) return true
  try {
    return window.sessionStorage?.getItem(MCP_AUTH_WINDOW_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function clearAuthWindowMarker() {
  try {
    window.sessionStorage?.removeItem(MCP_AUTH_WINDOW_STORAGE_KEY)
  } catch {
    // ignore
  }
  try {
    if (window.name === AUTH_WINDOW_NAME) window.name = ''
  } catch {
    // ignore
  }
}

/**
 * Open a blank full tab during the user click (avoids popup blockers).
 * Named so a later Connect / Finish reuses the same tab when it is still open.
 * Do not use noopener — the auth tab needs window.opener for postMessage when COOP allows it.
 *
 * @returns {Window|null}
 */
export function openBlankConnectTab() {
  if (typeof window === 'undefined') return null
  try {
    const tab = window.open('about:blank', AUTH_WINDOW_NAME) || null
    markAuthWindow(tab)
    return tab
  } catch {
    return null
  }
}

/**
 * @param {Window|null} win
 * @param {string} authorizeUrl
 * @returns {boolean}
 */
export function sendAuthWindowToAuthorizeUrl(win, authorizeUrl) {
  if (!win || win.closed) return false
  try {
    markAuthWindow(win)
    win.location.href = authorizeUrl
    win.focus?.()
    return true
  } catch {
    return false
  }
}

/** @deprecated use sendAuthWindowToAuthorizeUrl */
export function sendPopupToAuthorizeUrl(win, authorizeUrl) {
  return sendAuthWindowToAuthorizeUrl(win, authorizeUrl)
}

/**
 * Runs in the auth tab after connect_api redirects back to NF.
 * Notifies the opener when possible, then closes — never boot a second full app.
 *
 * @returns {boolean} true when this window handled an MCP connect return (do not boot the app)
 */
export function deliverMcpConnectResultToOpener() {
  if (typeof window === 'undefined') return false
  if (!isMcpAuthWindow()) return false

  const params = new URLSearchParams(window.location.search)
  const status = params.get('mcp_connected')
  const message = params.get('mcp_message')
  const hasResultParams = Boolean(status || message)

  const opener = window.opener && window.opener !== window ? window.opener : null
  if (opener && hasResultParams) {
    try {
      opener.postMessage(
        {
          source: MCP_CONNECT_MESSAGE_SOURCE,
          status,
          path: `${window.location.pathname}${window.location.search}`,
        },
        window.location.origin
      )
    } catch {
      // Opener may be unreachable; opener polling still picks up success
    }
  }

  clearAuthWindowMarker()

  try {
    window.close()
  } catch {
    // Browser may block close
  }
  return true
}

/**
 * @param {MessageEvent} event
 * @returns {{ status: string|null, path: string }|null}
 */
export function readMcpConnectMessage(event) {
  if (typeof window === 'undefined') return null
  if (event?.origin !== window.location.origin) return null
  const data = event?.data
  if (!data || data.source !== MCP_CONNECT_MESSAGE_SOURCE) return null
  if (typeof data.path !== 'string' || !data.path.startsWith('/')) return null
  return { status: data.status ?? null, path: data.path }
}
